import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { booksKeys } from "@/features/books/queries/booksQueryKeys";
import booksService from "@/features/books/services/booksService";
import type { CreateBookInput } from "@/features/books/types";

/**
 * Mutation hook for creating a new book
 * Invalidates books query on success and navigates to books page
 */
export function useCreateBook() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const mutation = useMutation({
        mutationFn: async (bookData: CreateBookInput) => {
            return await booksService.createBook(bookData);
        },
        onError: (error) => {
            // Handle duplicate book error specifically
            if (error instanceof Error && error.message.includes("already exists")) {
                toast.error("This book already exists in the library");
            } else {
                const errorMessage =
                    error instanceof Error ? error.message : "Failed to create book. Please try again.";
                toast.error(errorMessage);
            }
        },
        onSuccess: () => {
            // Invalidate books query to refresh the list
            queryClient.invalidateQueries({
                queryKey: booksKeys.all,
            });

            toast.success("Book added successfully");
            navigate("/books");
        },
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isPending: mutation.isPending,
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
    };
}
